import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import type { VesselWithRelations } from "@/hooks/useVessels";
import type {
  AssignmentStatus,
  PlanningLotRef,
  VesselAssignmentWithRelations,
} from "@/hooks/useVesselAssignments";

/**
 * Customer-portal Tank Schedule data, from get_client_tank_schedule().
 *
 * Clients can't read the planner tables directly (staff-only RLS since
 * 20261001150001), so everything comes through one SECURITY DEFINER call that
 * masks other clients' bars on the server: those rows arrive with
 * is_mine = false and no lot, client, volume, notes or label.
 *
 * Rows are mapped into the staff planner's shapes so TankPlanningTimeline,
 * AssignmentBar and OnDeckSidebar render them unchanged.
 */

interface ScheduleVesselRow {
  id: string;
  name: string;
  type: string;
  capacity_l: number | null;
  capacity_tons: number | null;
  preferred_capacity_unit: string | null;
  vessel_kind: string | null;
  vessel_subtypes: string[] | null;
  is_active: boolean;
  facility_id: string | null;
  facility: { id: string; code: string; name: string } | null;
}

interface ScheduleAssignmentRow {
  id: string;
  vessel_id: string | null;
  fill_date: string;
  projected_empty_date: string | null;
  actual_empty_date: string | null;
  status: AssignmentStatus;
  is_mine: boolean;
  wine_lot_id: string | null;
  planning_lot_id: string | null;
  fill_volume_l: number | null;
  notes: string | null;
  color: string | null;
  display_label: string | null;
  empty_reason: string | null;
  wine_lot: {
    id: string;
    code: string;
    varietal: string | null;
    vintage: number | null;
    current_volume_l: number | null;
    client: { id: string; name: string } | null;
  } | null;
  planning_lot: PlanningLotRef | null;
}

interface ScheduleResponse {
  vessels: ScheduleVesselRow[];
  assignments: ScheduleAssignmentRow[];
  on_deck: ScheduleAssignmentRow[];
  connections: { id: string; source_id: string; target_id: string }[];
}

/** Planner vessel row; the portal also carries the kind/subtype/unit columns the grid reads. */
export type ScheduleVessel = VesselWithRelations & {
  vessel_kind: string | null;
  vessel_subtypes: string[] | null;
  preferred_capacity_unit: string | null;
};

export interface ClientTankSchedule {
  vessels: ScheduleVessel[];
  assignments: VesselAssignmentWithRelations[];
  onDeck: VesselAssignmentWithRelations[];
  connections: { id: string; source_id: string; target_id: string }[];
}

function toVessel(v: ScheduleVesselRow): ScheduleVessel {
  return {
    ...v,
    // Not returned to clients — the grid doesn't need them.
    org_id: "",
    owner_client_id: null,
    owner_client: null,
    manufacturer: null,
    sub_location: null,
    vintrace_vessel_id: null,
    created_at: "",
    updated_at: "",
  };
}

function toAssignment(
  row: ScheduleAssignmentRow,
  vesselsById: Map<string, ScheduleVesselRow>,
): VesselAssignmentWithRelations {
  const vessel = row.vessel_id ? vesselsById.get(row.vessel_id) : undefined;
  return {
    id: row.id,
    org_id: "",
    // On Deck rows have no vessel; the planner types model that as an empty id.
    vessel_id: row.vessel_id ?? "",
    wine_lot_id: row.wine_lot_id,
    planning_lot_id: row.planning_lot_id,
    fill_date: row.fill_date,
    projected_empty_date: row.projected_empty_date,
    actual_empty_date: row.actual_empty_date,
    status: row.status,
    fill_volume_l: row.fill_volume_l,
    empty_volume_l: null,
    notes: row.notes,
    color: row.color,
    source_assignment_id: null,
    empty_reason: row.empty_reason,
    empty_reason_notes: null,
    display_label: row.display_label,
    created_by: null,
    created_at: "",
    updated_at: "",
    vessel: vessel
      ? { id: vessel.id, name: vessel.name, type: vessel.type, capacity_l: vessel.capacity_l }
      : { id: "", name: "", type: "", capacity_l: null },
    wine_lot: row.wine_lot,
    planning_lot: row.planning_lot,
    is_mine: row.is_mine,
  };
}

export function useClientTankSchedule(start: Date, end: Date) {
  const startKey = format(start, "yyyy-MM-dd");
  const endKey = format(end, "yyyy-MM-dd");

  return useQuery({
    queryKey: ["client-tank-schedule", startKey, endKey],
    placeholderData: keepPreviousData,
    // Realtime can't deliver other clients' changes to a client (staff-only RLS),
    // so poll. 20s keeps the view current without hammering the database.
    refetchInterval: 20_000,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<ClientTankSchedule> => {
      const { data, error } = await supabase.rpc("get_client_tank_schedule", {
        p_start: startKey,
        p_end: endKey,
      });
      if (error) throw error;

      const res = (data ?? {}) as unknown as Partial<ScheduleResponse>;
      const vesselRows = res.vessels ?? [];
      const vesselsById = new Map(vesselRows.map((v) => [v.id, v]));

      return {
        vessels: vesselRows.map(toVessel),
        assignments: (res.assignments ?? []).map((a) => toAssignment(a, vesselsById)),
        onDeck: (res.on_deck ?? []).map((a) => toAssignment(a, vesselsById)),
        connections: res.connections ?? [],
      };
    },
  });
}
